import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { motion } from 'framer-motion'
import { atom, useAtomValue, useSetAtom, PrimitiveAtom } from 'jotai'

import { cn } from '@/lib/utils'

interface TabsState {
  underlineWidth: PrimitiveAtom<number>
  underlineLeft: PrimitiveAtom<number>
  activeTab: PrimitiveAtom<string | null>
  tabsRef: PrimitiveAtom<Map<string, HTMLButtonElement>>
}

interface TabsContextValue {
  tabsState: TabsState
  tabsId: string
}

const TabsContext = React.createContext<TabsContextValue | null>(null)

const useTabsContext = () => {
  const context = React.useContext(TabsContext)
  if (!context) {
    throw new Error('Tabs components must be used within a Tabs provider')
  }
  return context
}

// Função para criar um novo estado de tabs isolado
const createTabsState = (): TabsState => ({
  underlineWidth: atom(0),
  underlineLeft: atom(0),
  activeTab: atom<string | null>(null),
  tabsRef: atom<Map<string, HTMLButtonElement>>(new Map())
})

const Tabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>
>(({ onValueChange, defaultValue, value, ...props }, ref) => {
  // Cria um estado isolado para esta instância de tabs
  const tabsState = React.useMemo(() => createTabsState(), [])
  const tabsId = React.useMemo(() => `tabs-${Math.random().toString(36).slice(2, 11)}`, [])
  // const tabsId = React.useMemo(() => `tabs-${Math.random().toString(36).substr(2, 9)}`, [])

  const setUnderlineWidth = useSetAtom(tabsState.underlineWidth)
  const setUnderlineLeft = useSetAtom(tabsState.underlineLeft)
  const setActiveTab = useSetAtom(tabsState.activeTab)
  const tabsRef = useAtomValue(tabsState.tabsRef)

  const updateUnderlinePosition = React.useCallback(
    (tabValue: string) => {
      const activeTabElement = tabsRef.get(tabValue)
      if (activeTabElement) {
        setUnderlineWidth(activeTabElement.offsetWidth)
        setUnderlineLeft(activeTabElement.offsetLeft)
      }
    },
    [tabsRef, setUnderlineWidth, setUnderlineLeft]
  )

  const handleValueChange = React.useCallback(
    (tabValue: string) => {
      setActiveTab(tabValue)
      onValueChange?.(tabValue)
      updateUnderlinePosition(tabValue)
    },
    [setActiveTab, onValueChange, updateUnderlinePosition]
  )

  // Reset e inicializa o underline quando o componente monta ou quando defaultValue/value muda
  React.useEffect(() => {
    const initialValue = value || defaultValue
    if (initialValue) {
      // Pequeno delay para garantir que as refs estejam prontas
      const timer = setTimeout(() => {
        setActiveTab(initialValue)
        updateUnderlinePosition(initialValue)
      }, 0)

      return () => clearTimeout(timer)
    } else {
      // Reset se não há valor inicial
      setUnderlineWidth(0)
      setUnderlineLeft(0)
    }
  }, [defaultValue, value, setActiveTab, setUnderlineLeft, setUnderlineWidth, updateUnderlinePosition])

  return (
    <TabsContext.Provider value={{ tabsState, tabsId }}>
      <TabsPrimitive.Root
        ref={ref}
        onValueChange={handleValueChange}
        defaultValue={defaultValue}
        value={value}
        {...props}
      />
    </TabsContext.Provider>
  )
})
Tabs.displayName = TabsPrimitive.Root.displayName

type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  /** Ativa scroll horizontal com fades laterais */
  scrollable?: boolean
  /** Esconde a barra de rolagem (default: true) */
  hideScrollbar?: boolean
  /** Sobe a underline N px para não confundir com o scrollbar (default: 6) */
  underlineOffset?: number
}

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(
  ({ className, children, scrollable = true, hideScrollbar = true, underlineOffset = 6, ...props }, ref) => {
    const { tabsState } = useTabsContext()
    const underLineWidth = useAtomValue(tabsState.underlineWidth)
    const underlineLeft = useAtomValue(tabsState.underlineLeft)

    // refs e fades só se scrollable
    const containerRef = React.useRef<HTMLDivElement>(null)
    const leftFadeRef = React.useRef<HTMLDivElement>(null)
    const rightFadeRef = React.useRef<HTMLDivElement>(null)

    const updateFades = React.useCallback(() => {
      if (!scrollable) return
      const el = containerRef.current
      const l = leftFadeRef.current
      const r = rightFadeRef.current
      if (!el || !l || !r) return
      l.style.opacity = el.scrollLeft > 8 ? '1' : '0'
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8
      r.style.opacity = atEnd ? '0' : '1'
    }, [scrollable])

    React.useEffect(() => {
      if (!scrollable) return
      updateFades()
      const onResize = () => updateFades()
      window.addEventListener('resize', onResize)
      return () => window.removeEventListener('resize', onResize)
    }, [scrollable, updateFades])

    const ListInner = (
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          'inline-flex h-10 items-center justify-start relative ',
          // quando scrollable, garanta largura mínima p/ permitir scroll
          scrollable && 'min-w-full',
          // dá um pequeno padding-bottom pra separar o texto do “pé” do container
          'pb-1',
          className
        )}
        {...props}
      >
        {children}

        <motion.div
          className={cn('absolute bg-primary  bottom-0')}
          initial={false}
          animate={{ width: underLineWidth, left: underlineLeft }}
          // empurra a underline alguns px pra longe do scrollbar
          style={{ bottom: underlineOffset }}
          transition={{ type: 'spring', stiffness: 500, damping: 30, bounce: 0 }}
        />
      </TabsPrimitive.List>
    )

    if (!scrollable) return ListInner

    return (
      <div className="relative">
        {/* fades laterais */}
        <div
          ref={leftFadeRef}
          className=" pointer-events-none absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent opacity-0 transition-opacity duration-300"
        />
        <div
          ref={rightFadeRef}
          className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent opacity-0 transition-opacity duration-300"
        />

        {/* container com scroll */}
        <div
          ref={containerRef}
          onScroll={updateFades}
          className={cn(
            'overflow-x-auto scroll-smooth ',
            // reserva espaço estável pro scrollbar quando ele existe
            '[scrollbar-gutter:stable_both-edges]',
            hideScrollbar && 'scrollbar-hide'
          )}
        >
          {ListInner}
        </div>
      </div>
    )
  }
)

TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, value, ...props }, ref) => {
  const { tabsState } = useTabsContext()
  const setTabsRef = useSetAtom(tabsState.tabsRef)
  const localRef = React.useRef<HTMLButtonElement>(null)

  // Combina as referências local e externa
  React.useImperativeHandle(ref, () => localRef.current!)

  React.useEffect(() => {
    if (localRef.current && value) {
      setTabsRef((prev: Map<string, HTMLButtonElement>) => new Map(prev.set(value, localRef.current!)))
    }

    return () => {
      if (value) {
        setTabsRef((prev: Map<string, HTMLButtonElement>) => {
          const newMap = new Map(prev)
          newMap.delete(value)
          return newMap
        })
      }
    }
  }, [value, setTabsRef])

  return (
    <TabsPrimitive.Trigger
      ref={localRef}
      value={value}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',

        'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:scale-x-0 after:bg-muted-foreground after:transition-transform after:duration-300 hover:after:scale-x-50',
        'relative isolate overflow-hidden',
        'hover:text-foreground',

        // ativo
        'data-[state=active]:font-bold data-[state=active]:text-foreground',
        'data-[state=active]:-translate-y-px data-[state=active]:rounded-xl',

        // <<< camada do fade no bottom (metade de altura)
        'data-[state=active]:before:content-[""] data-[state=active]:before:absolute',
        'data-[state=active]:before:inset-x-0 data-[state=active]:before:bottom-0',
        'data-[state=active]:before:h-1/2',
        'data-[state=active]:before:bg-gradient-to-t',
        'data-[state=active]:before:from-primary/20 data-[state=active]:before:to-transparent',

        className
      )}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn('mt-2 focus-visible:outline-none', className)} {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
